import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ApiFout, api } from '@/lib/api';
import type { LiveTranscriptClaim } from '@/lib/api-types';
import { Loader2, AlertTriangle, Radio, Stethoscope, User, Languages } from 'lucide-react';

interface LiveMessage {
  id: string;
  speaker: string;
  role: 'arts' | 'patient' | null;
  text: string;
  translatedText?: string;
  timestamp: string;
}

const LANGUAGES: { code: string; label: string; name: string }[] = [
  { code: 'nl', label: 'Nederlands', name: 'Nederlands' },
  { code: 'en', label: 'English', name: 'Engels' },
  { code: 'de', label: 'Deutsch', name: 'Duits' },
  { code: 'fr', label: 'Français', name: 'Frans' },
  { code: 'es', label: 'Español', name: 'Spaans' },
  { code: 'it', label: 'Italiano', name: 'Italiaans' },
  { code: 'pt', label: 'Português', name: 'Portugees' },
  { code: 'pl', label: 'Polski', name: 'Pools' },
  { code: 'tr', label: 'Türkçe', name: 'Turks' },
  { code: 'ar', label: 'العربية', name: 'Arabisch' },
  { code: 'uk', label: 'Українська', name: 'Oekraïens' },
  { code: 'ru', label: 'Русский', name: 'Russisch' },
  { code: 'zh', label: '中文', name: 'Chinees' },
];

export default function LiveTranscriptPage() {
  const { token } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [practiceName, setPracticeName] = useState('');
  const [messages, setMessages] = useState<LiveMessage[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [targetLang, setTargetLang] = useState<string>('nl');
  const [translations, setTranslations] = useState<Record<string, string>>({});
  const translatingRef = useRef<Map<string, string>>(new Map()); // msgId -> source text being translated
  const lastTextByIdRef = useRef<Map<string, string>>(new Map());
  const bubbleCounterRef = useRef(0);

  useEffect(() => {
    if (!token) return;

    void (async () => {
      try {
        // Het inwisselen is atomair: één statement met de voorwaarde erin. In de bron
        // las claim-live-transcript scan_count, controleerde op nul en schreef daarna
        // terug -- twee telefoons die tegelijk scanden konden er beide langs, en dan
        // keken er twee mensen mee naar hetzelfde consult.
        const antwoord = await api.post<LiveTranscriptClaim>(
          `/live-transcript/${encodeURIComponent(token)}/claim`,
        );
        setPracticeName(antwoord.practiceName);
      } catch (fout) {
        setError(
          fout instanceof ApiFout ? fout.message : 'Verbindingsfout. Probeer opnieuw.',
        );
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  /**
   * Meelezen via server-sent events.
   *
   * Vervangt `supabase.channel(...).on('broadcast', ...)`. Supabase Realtime
   * verdwijnt met Supabase; de vervanging staat in
   * apps/api/src/lib/live-transcript.ts -- in-memory pub/sub, zonder de berichten op
   * te slaan. Een consultgesprek hoort niet langer te bestaan dan de seconden waarin
   * het over de lijn gaat.
   *
   * EventSource herstelt een verbroken verbinding zelf, en dat is de reden om SSE te
   * kiezen boven een websocket: dit is eenrichtingsverkeer en de browser doet het
   * herstel.
   */
  const [verbonden, setVerbonden] = useState(false);

  useEffect(() => {
    if (!token || error || loading) return;

    const bron = new EventSource(`/api/live-transcript/${encodeURIComponent(token)}/stream`);

    bron.addEventListener('open', () => setVerbonden(true));

    bron.addEventListener('message', (gebeurtenis) => {
      const msg = JSON.parse((gebeurtenis as MessageEvent<string>).data) as LiveMessage;

      // Alleen het nieuwe deel tonen. De arts stuurt bij elke afronding de volledige
      // regel, en zonder deze vergelijking zou dezelfde tekst zich herhalen.
      const prevText = lastTextByIdRef.current.get(msg.id) || '';
      let deltaText = msg.text;
      if (prevText && msg.text.startsWith(prevText)) {
        deltaText = msg.text.slice(prevText.length).trim();
        if (!deltaText) return;
      }
      lastTextByIdRef.current.set(msg.id, msg.text);

      bubbleCounterRef.current += 1;
      const bubble: LiveMessage & { bubbleKey: string } = {
        ...msg,
        text: deltaText,
        bubbleKey: `${msg.id}-${bubbleCounterRef.current}`,
      };
      setMessages((prev) => [bubble as LiveMessage, ...prev]);
    });

    bron.addEventListener('clear', () => {
      setMessages([]);
      lastTextByIdRef.current.clear();
    });

    bron.addEventListener('error', () => {
      // EventSource probeert zelf opnieuw; dit is er om de gebruiker te laten zien
      // dat er even niets doorkomt in plaats van hem naar een stil scherm te laten
      // kijken.
      setVerbonden(false);
    });

    return () => bron.close();
  }, [token, error, loading]);

  // Vertalen zodra er een bericht bijkomt of de taal wijzigt.
  useEffect(() => {
    if (targetLang === 'nl') return;
    const langName = LANGUAGES.find((l) => l.code === targetLang)?.name || targetLang;

    messages.forEach((m) => {
      const source = m.text;
      if (!source) return;
      const key = (m as LiveMessage & { bubbleKey?: string }).bubbleKey || m.id;
      const cacheKey = `${key}::${targetLang}::${source}`;
      if (translations[cacheKey]) return;
      if (translatingRef.current.get(key) === cacheKey) return;
      translatingRef.current.set(key, cacheKey);

      /**
       * Via een publieke route die op het sessietoken autoriseert.
       *
       * In de bron ging dit naar translate-to-nl met de anon-key uit de bundel, dus
       * volledig onbeschermd: iedereen op internet kon die functie aanroepen op
       * kosten van iSOFT. /api/ai/translate kan hier niet dienen -- die vereist een
       * praktijksessie, en een patiënt heeft die niet.
       */
      void api
        .post<{ translated: string }>(
          `/live-transcript/${encodeURIComponent(token ?? '')}/translate`,
          { text: source, targetLanguage: langName },
        )
        .then((antwoord) => {
          if (antwoord.translated) {
            setTranslations((prev) => ({ ...prev, [cacheKey]: antwoord.translated }));
          }
        })
        .catch(() => {
          // Een mislukte vertaling laat de oorspronkelijke tekst staan; dat is beter
          // dan een foutmelding op het scherm van een patiënt.
          translatingRef.current.delete(key);
        });
    });
  }, [messages, targetLang, translations, token]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }, [messages, translations]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="max-w-md w-full text-center space-y-4">
          <AlertTriangle className="h-12 w-12 text-destructive mx-auto" />
          <h1 className="text-xl font-semibold">Link niet geldig</h1>
          <p className="text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="px-4 py-3 bg-primary/10 border-b flex items-center gap-2 shrink-0">
        {/*
          Het icoon pulseert alleen bij een open verbinding. Een verbroken stream zag
          er in de bron hetzelfde uit als een consult waarin even niets werd gezegd;
          nu is dat verschil te zien. EventSource herstelt zichzelf, dus dit is een
          waarschuwing en geen foutmelding.
        */}
        <Radio
          className={
            verbonden ? 'h-4 w-4 text-primary animate-pulse' : 'h-4 w-4 text-muted-foreground'
          }
        />
        <div className="flex-1 min-w-0">
          <h1 className="text-sm font-semibold truncate">
            Live transcriptie
            {!verbonden && (
              <span className="ml-2 text-[11px] font-normal text-muted-foreground">
                verbinding herstellen...
              </span>
            )}
          </h1>
          {practiceName && <p className="text-[11px] text-muted-foreground truncate">{practiceName}</p>}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Languages className="h-3.5 w-3.5 text-muted-foreground" />
          <select
            value={targetLang}
            onChange={(e) => setTargetLang(e.target.value)}
            className="text-xs bg-background border border-border rounded px-1.5 py-1 max-w-[110px]"
            aria-label="Vertaaltaal"
          >
            {LANGUAGES.map((l) => (
              <option key={l.code} value={l.code}>
                {l.label}
              </option>
            ))}
          </select>
        </div>
      </header>
      {/* Legenda: wie is wie */}
      <div className="px-3 py-1.5 border-b bg-background flex items-center justify-center gap-4 shrink-0">
        <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
          <span className="inline-flex items-center gap-1 rounded-full bg-primary text-primary-foreground px-1.5 py-0.5 text-[10px] font-bold uppercase">
            <Stethoscope className="h-2.5 w-2.5" /> Dokter
          </span>
          links
        </span>
        <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
          <span className="inline-flex items-center gap-1 rounded-full bg-secondary text-secondary-foreground border border-border px-1.5 py-0.5 text-[10px] font-bold uppercase">
            <User className="h-2.5 w-2.5" /> Patiënt
          </span>
          rechts
        </span>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-2 space-y-2 bg-muted/10">
        {messages.length === 0 ? (
          <div className="h-full flex items-center justify-center text-center text-sm text-muted-foreground italic px-6">
            Wacht op het gesprek… De berichten verschijnen hier live zodra de zorgverlener spreekt.
          </div>
        ) : (
          messages.map((m, idx) => {
            const isDoctor = m.role === 'arts';
            const isPatient = m.role === 'patient';
            const key = (m as LiveMessage & { bubbleKey?: string }).bubbleKey || `${m.id}-${idx}`;
            const cacheKey = `${key}::${targetLang}::${m.text}`;
            const display =
              targetLang === 'nl'
                ? m.text
                : translations[cacheKey] || m.text;
            const showOriginal = targetLang !== 'nl' && translations[cacheKey] && translations[cacheKey] !== m.text;
            const isTranslating = targetLang !== 'nl' && !translations[cacheKey];
            const speakerLabel = isDoctor ? 'Dokter' : isPatient ? 'Patiënt' : (m.speaker || 'Onbekende spreker');
            return (
              <div key={key} className={`flex ${isDoctor ? 'justify-start' : 'justify-end'}`}>
                <div className="max-w-[88%] space-y-1">
                  {/* Duidelijk sprekerlabel boven de ballon */}
                  <div className={`flex items-center gap-1.5 ${isDoctor ? 'justify-start' : 'justify-end'}`}>
                    <span
                      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide ${
                        isDoctor
                          ? 'bg-primary text-primary-foreground'
                          : isPatient
                            ? 'bg-secondary text-secondary-foreground border border-border'
                            : 'bg-muted text-muted-foreground border border-border'
                      }`}
                    >
                      {isDoctor ? <Stethoscope className="h-3 w-3" /> : <User className="h-3 w-3" />}
                      {speakerLabel}
                    </span>
                    <span className="text-[10px] text-muted-foreground">
                      {new Date(m.timestamp).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                  <div
                    className={`rounded-lg px-3 py-2 border-2 ${
                      isDoctor
                        ? 'bg-primary/10 border-primary/50 rounded-tl-sm'
                        : isPatient
                          ? 'bg-card border-border rounded-tr-sm'
                          : 'bg-muted/40 border-dashed border-border rounded-tr-sm'
                    }`}
                  >
                    <p className="text-base leading-snug text-foreground whitespace-pre-wrap">{display}</p>
                    {isTranslating && (
                      <p className="text-[10px] italic text-muted-foreground mt-1 flex items-center gap-1">
                        <Loader2 className="h-2.5 w-2.5 animate-spin" /> vertalen…
                      </p>
                    )}
                    {showOriginal && (
                      <p className="text-[11px] text-muted-foreground mt-1 italic whitespace-pre-wrap">{m.text}</p>
                    )}
                  </div>
                </div>
              </div>
            );
          })

        )}
      </div>
      <footer className="px-3 py-2 border-t text-[10px] text-muted-foreground text-center shrink-0">
        Deze pagina toont het gesprek live mee.
      </footer>
    </div>
  );
}